import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router";
import { RootState, AppDispatch } from "../store";
import {
  removeFromCart,
  updateQuantity,
  clearCart,
} from "../store/slices/cartSlice";

const Cart: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { items, totalItems, totalPrice } = useSelector(
    (state: RootState) => state.cart,
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const handleRemoveItem = (productId: number) => {
    dispatch(removeFromCart(productId));
  };

  const handleUpdateQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity > 0) {
      dispatch(updateQuantity({ id: productId, quantity: newQuantity }));
    }
  };

  const handleCheckout = () => {
    if (!user) navigate("/login");
    else navigate("/checkout");
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1
        data-test="cart-heading"
        className="text-3xl font-bold mb-8 text-[#F0EEFF]"
      >
        Your Cart
      </h1>

      {items.length === 0 ? (
        <div className="text-center">
          <p data-test="empty-cart" className="text-xl text-[#7A7A8C] mb-4">
            Your cart is empty
          </p>
          <Link
            to="/products"
            data-test="continue-shopping"
            className="bg-[#FF4500] text-white hover:text-white px-6 py-3 rounded hover:bg-[#FF6B47]"
          >
            Continue Shopping
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-4">
            {items.map((item) => (
              <div
                key={item.id}
                data-test={`cart-item`}
                className="flex items-center bg-[#13131C] border border-[#252535] rounded-lg p-4"
              >
                <img
                  src={item.img}
                  alt={item.name}
                  className="w-24 h-24 object-cover rounded mr-4"
                />

                <div className="flex-grow">
                  <h3
                    data-test="product-name"
                    className="text-lg font-semibold"
                  >
                    {item.name}
                  </h3>
                  <p data-test="product-price" className="text-[#7A7A8C]">
                    ${item.price.toFixed(2)}
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <button
                    data-test={`quantity-decrement`}
                    onClick={() =>
                      handleUpdateQuantity(item.id, item.quantity - 1)
                    }
                    className="bg-[#1E1E2C] border border-[#252535] text-[#F0EEFF] px-3 py-1 rounded hover:border-[#FF4500]/40 transition-colors"
                  >
                    -
                  </button>
                  <span data-test="cart-item-qty">{item.quantity}</span>
                  <button
                    data-test={`quantity-increment`}
                    onClick={() =>
                      handleUpdateQuantity(item.id, item.quantity + 1)
                    }
                    className="bg-[#1E1E2C] border border-[#252535] text-[#F0EEFF] px-3 py-1 rounded hover:border-[#FF4500]/40 transition-colors"
                  >
                    +
                  </button>
                  <button
                    data-test={`remove-item`}
                    onClick={() => handleRemoveItem(item.id)}
                    className="text-[#FF4500] hover:text-[#FF6B47]"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#13131C] border border-[#252535] rounded-lg p-6">
            <h2
              data-test="cart-summary-title"
              className="text-2xl font-bold mb-4"
            >
              Cart Summary
            </h2>

            <div className="flex justify-between mb-2">
              <span>Total Items:</span>
              <span data-test="cart-total-items">{totalItems}</span>
            </div>

            <div className="flex justify-between mb-4 font-bold">
              <span>Total Price:</span>
              <span data-test="cart-total">${totalPrice.toFixed(2)}</span>
            </div>

            <button
              data-test="checkout-btn"
              onClick={handleCheckout}
              className="w-full bg-[#FF4500] text-white py-3 rounded hover:bg-[#FF6B47]"
            >
              Proceed to Checkout
            </button>

            <button
              data-test="clear-cart-btn"
              onClick={() => dispatch(clearCart())}
              className="w-full mt-2 bg-red-500 text-white py-3 rounded hover:bg-red-600"
            >
              Clear Cart
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
